import { useToast } from '@ui/toasts/useToastManager'
import Button from '@ui/buttons/Button'
import ConfigDebugModal from 'features/debug/ConfigDebugModal'
import SummaryDebugModal from 'features/debug/SummaryDebugModal'
import ButtonsFixed, { SpaceButtons } from '@ui/buttons/ButtonsFixed'
import Page from '@shell/Page'
import Header from '@ui/elements/Header'
import ExtraDealModal from '@order/modals/ExtraDealModal'
import useBundleType from '@order/hooks/useBundleType'
import useCartState from '@order/hooks/useCartState'
import { useSyncActiveItem } from '@screens/configuration/lenses/useSyncActiveItem'
import { useDealSelect } from '@order/hooks/useDealTypeModal'
import useOrderConfigure from '@order/hooks/useOrderConfigure'
import { useOrderState } from '@order/hooks/useOrderState'
import useRestartOrder from '@order/hooks/useRestartOrder'
import { useCurrentScenario } from '@order/hooks/useScenario'
import { useDiscountsEnabled } from '@order/useDiscounts'
import { router, useNavigation } from 'expo-router'
import useExtraDeal from 'hooks/useExtraDeal'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import useSession, {
  getSessionDealContext,
  useResolvedDealsContext,
} from 'hooks/useSession'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import OrderSummaryBody from './OrderSummaryBody'
import { getFlow } from '@order/utils/getFlow'
import { getImageUrl } from 'config/getImageUrl'
import { getFirstExistingKey } from 'utils/i18nUtils'
import { useSaveOrderDefaults } from '@order/hooks/useSaveOrderDefaults'
import { useIsFocused } from '@ui/hooks/useIsFocused'

const HEIGHT_BOTTOM_BUTTONS = 280

export default function OrderSummary() {
  const { t, i18n } = useTranslation(['order', 'modals', 'deals-modals', 'ui', 'status'])
  const isFocused = useIsFocused()
  const text = 'order:summary'
  const config = useRemoteConfig()
  const items = useOrderState((s) => s.order.items)
  const packageCfg = useOrderState((s) => s.order.packageCfg)
  const setPendingOrder = useSession((s) => s.setPendingOrder)
  const resetItemCompleted = useOrderState((s) => s.resetItemCompleted)
  useSyncActiveItem()
  const addOrder = useCartState((s) => s.addOrder)
  const { saveOrderDefaults } = useSaveOrderDefaults()

  const { isCompleted, next } = useMemo(() => {
    const next = items.find((i) => !i.isCompleted)

    return {
      next: items.length > 1 ? next : undefined,
      isCompleted: !next,
    }
  }, [items])
  // const next = items.find((i) => !i.isCompleted)

  const switchActiveItem = useOrderState((s) => s.switchActiveItem)
  const [extraDealModal, setExtraDealModal] = useState(false)

  const result = useOrderState((s) => s.result)

  const navigation = useNavigation()
  useEffect(() => {
    if (isFocused) {
      saveOrderDefaults()
    }
  }, [saveOrderDefaults, isFocused])

  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (e) => {
      if (e.data.action.type === 'GO_BACK' || e.data.action.type === 'RESET') {
        resetItemCompleted()
      }
    })
    return unsubscribe
  }, [navigation])

  const { selectDeal } = useDealSelect()
  const { bundleType } = useBundleType()
  const submit = useOrderConfigure(!!next)
  const currentPackage = packageCfg[bundleType]
  const scenario = useCurrentScenario()
  const discountsEnabled = useDiscountsEnabled(scenario)

  const extra = useExtraDeal()

  const handlePayloadError = () => {
    if (!submit.payloadError) {
      return false
    }
    const specific = `toasts:error.payload.${submit.payloadError}.desc`
    const general = `toasts:error.payload.desc`
    const hasSpecific = i18n.exists(specific)

    toast({
      variant: 'error',
      title: t('toasts:error.payload.title'),
      text: hasSpecific ? t(specific) : t(general),
    })

    return true
  }

  const handleCheckout = () => {
    setExtraDealModal(false)

    const hasPayloadError = handlePayloadError()
    if (hasPayloadError) {
      return
    }

    if (!result?.isValid || result.orders.length !== items.length) {
      // Allow manual submit
      submit.manualSubmit()
      return
    }

    if (discountsEnabled) {
      router.navigate('/order/discounts')
      return
    }

    router.navigate('/order/billing')
  }

  const order = useOrderState((s) => s.order)
  const discounts = useOrderState((s) => s.discounts)
  const deals = useResolvedDealsContext()

  const { restart } = useRestartOrder()

  const handleExtraDeal = () => {
    if (!extra.deal) {
      return
    }

    const hasExtraFilter =
      !!extra.deal.filter?.tag ||
      !!extra.deal.filter?.type ||
      !!extra.deal.filter?.tagsExcluded?.length ||
      !!extra.deal.filter?.price
    const isPending = extra.deal.type === 'tgbo' && hasExtraFilter
    // Handle pending order
    if (isPending) {
      const savedOrderId = addOrder({
        order,
        result,
        discounts,
        deals,
      })

      setPendingOrder({
        deal: getSessionDealContext(extra.deal, {
          basePackageId: extra.deal.packages[0],
        }),
        savedOrderId,
        uiMode: extra.mode === 'select' ? 'select' : 'default',
      })
      restart({
        clearPendingOrder: false,
      })
      return
    }

    if (extra.mode === 'select') {
      router.push({
        pathname: '/configuration/deal/select',
        params: {
          bundleType: 'extra',
          tag: extra.deal.filter?.tag,
          type: extra.deal.filter?.type,
          tags_excluded: extra.deal.filter?.tagsExcluded?.join(','),
          price: extra.deal.filter?.price,
        },
      })
      return
    }

    selectDeal({
      deal: extra.deal,
      bundle: 'extra',
      type: currentPackage?.type,
      onSelected: () =>
        router.push({
          pathname: '/frame/select',
          params: { bundleType: 'extra' },
        }),
    })
  }

  const toast = useToast()

  const handleNext = () => {
    if (!config) {
      return
    }

    if (next) {
      const flow = getFlow(config, next?.flowName)
      const frameStepId =
        flow.stepModes.find((m) => m.mode === 'frame')?.initialStep ||
        flow.stepModes[0]?.initialStep
      switchActiveItem(next.id)
      // Configure nth frame
      router.push({
        pathname: `/configuration/steps/${frameStepId}`,
        params: {
          orderItemId: next.id,
          mode: 'frame',
        },
      })
      return
    }

    const shouldShowExtra =
      bundleType !== 'extra' && extra.isValid && extra.deal

    if (shouldShowExtra && extra.mode === 'pending' && !result?.isValid) {
      const payloadError = handlePayloadError()
      if (!payloadError) {
        // Try manual submit if we have no payload error (not sure if we can reach this point)
        submit.manualSubmit()
      }
      return
    }
    if (shouldShowExtra) {
      setExtraDealModal(true)
      return
    }

    handleCheckout()
    return
  }

  const handleSkipExtraDeal = () => {
    setPendingOrder(null)
    handleCheckout()
  }

  const isLoading = submit.isLoading

  return (
    <>
      <Page stage="order">
        <Header
          title={t(`${text}.header.title`)}
          desc={t(`${text}.header.desc`)}
        />
        <OrderSummaryBody isCompleted={isCompleted} isLoading={isLoading} />
        <SpaceButtons height={HEIGHT_BOTTOM_BUTTONS} />
      </Page>
      <ButtonsFixed.Gradient height={HEIGHT_BOTTOM_BUTTONS} />
      <ButtonsFixed.Buttons>
        <Button
          variant={!next ? 'filled' : 'outlined'}
          label={t('ui:continue')}
          iconEnd="arrow-narrow-right"
          loading={isLoading}
          disabled={submit.isError}
          onPress={handleNext}
        />
      </ButtonsFixed.Buttons>
      {extra.deal && (
        <ExtraDealModal
          title={extra.deal.title}
          desc={extra.deal.desc || undefined}
          body={extra.deal.body || undefined}
          image={getImageUrl(extra.deal.banner?.url || extra.deal.image?.url)}
          badge={{
            text: t(
              getFirstExistingKey(i18n, [
                `deals-modals:extraDeal.custom.${extra.deal.translationPath}.badge`,
                'modals:extraDeal.badge',
              ]) || ''
            ),
            variant: 'success',
          }}
          open={extraDealModal}
          onSkip={handleSkipExtraDeal}
          onAccept={handleExtraDeal}
          close={() => setExtraDealModal(false)}
        />
      )}
      <ConfigDebugModal />
      <SummaryDebugModal />
    </>
  )
}
